// src/services/paymentGateway.js
//
// Provider-agnostic card entry. The backend decides whether Square or Stripe is
// live (dashboard toggle -> GET /payments/config); this module loads that
// provider's SDK, mounts its card field, and hands back an opaque token the API
// accepts as `source_id` either way.
//
// Callers should never branch on the provider — that is the whole point.
import { API_URL } from '../api';

const SQUARE_SDK = {
  production: 'https://web.squarecdn.com/v1/square.js',
  sandbox: 'https://sandbox.web.squarecdn.com/v1/square.js',
};

const STRIPE_SDK = 'https://js.stripe.com/v3/';

// Square encodes the credential family in the public application ID. Use that
// as the final authority so a stale backend `environment` value cannot pair a
// production ID with the sandbox SDK (or the reverse).
export const resolveSquareEnvironment = (applicationId, configuredEnvironment) => {
  if (applicationId?.startsWith('sandbox-')) return 'sandbox';
  if (applicationId?.startsWith('sq0idp-')) return 'production';
  return configuredEnvironment === 'production' ? 'production' : 'sandbox';
};

export const isPaymentInitializationTimeout = (error) =>
  /unable to be initialized in time|initialization timed? out|initialization timeout/i
    .test(error?.message || '');

const wait = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

// Square owns the internal initialization deadline and does not expose a way
// to extend it. A small bounded retry window recovers from transient/slow
// connections without leaving checkout in an infinite loading state.
export const retryPaymentInitialization = async (
  operation,
  { delays = [800, 1800], onRetry } = {}
) => {
  let attempt = 0;

  while (true) {
    try {
      return await operation();
    } catch (error) {
      if (!isPaymentInitializationTimeout(error) || attempt >= delays.length) {
        throw error;
      }

      if (onRetry) await onRetry(error, attempt + 1);
      await wait(delays[attempt]);
      attempt += 1;
    }
  }
};

const loadScript = (src, marker) =>
  new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-payment-sdk="${marker}"]`);

    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('SDK failed to load')), { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.paymentSdk = marker;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error('SDK failed to load'));
    document.head.appendChild(script);
  });

class PaymentGatewayService {
  constructor() {
    this.config = null;
    this.configPromise = null;
    this.instance = null;
  }

  /**
   * Which provider is live, and its publishable SDK settings.
   * Cached for the page lifetime — switching providers is an admin action that
   * warrants a reload anyway.
   */
  async loadConfig({ force = false } = {}) {
    if (force) {
      this.config = null;
      this.configPromise = null;
      this.instance = null;
    }

    if (this.config) return this.config;

    if (!this.configPromise) {
      this.configPromise = fetch(`${API_URL}/payments/config`, {
        headers: { Accept: 'application/json' },
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error('Could not load payment settings.');
          }
          this.config = await response.json();
          return this.config;
        })
        .catch((error) => {
          this.configPromise = null;
          throw error;
        });
    }

    return this.configPromise;
  }

  get provider() {
    return this.config?.provider ?? null;
  }

  /** Whether the live provider can bill a donor-chosen recurring amount. */
  get supportsRecurringDonations() {
    return Boolean(this.config?.supports_recurring_donations);
  }

  async initialize() {
    const config = await this.loadConfig();

    if (!config.ready) {
      throw new Error('Payments are not configured. Please try again later.');
    }

    if (this.instance) return this.instance;

    this.instance = config.provider === 'stripe'
      ? await this.initStripe(config.config)
      : await this.initSquare(config.config);

    return this.instance;
  }

  async initSquare({ application_id: applicationId, location_id: locationId, environment }) {
    if (!applicationId || !locationId) {
      throw new Error('Square is missing its application or location id.');
    }

    const resolvedEnvironment = resolveSquareEnvironment(applicationId, environment);
    await loadScript(SQUARE_SDK[resolvedEnvironment], `square-${resolvedEnvironment}`);

    if (!window.Square) {
      throw new Error('Square Web Payments SDK not loaded');
    }

    return {
      provider: 'square',
      applicationId,
      locationId,
      payments: window.Square.payments(applicationId, locationId),
    };
  }

  async initStripe({ publishable_key: publishableKey }) {
    if (!publishableKey) {
      throw new Error('Stripe is missing its publishable key.');
    }

    await loadScript(STRIPE_SDK, 'stripe');

    if (!window.Stripe) {
      throw new Error('Stripe.js not loaded');
    }

    const stripe = window.Stripe(publishableKey);

    return { provider: 'stripe', stripe, elements: stripe.elements() };
  }

  /**
   * Mount a card field into `#${containerId}`.
   *
   * @returns {Promise<object>} an opaque handle to pass back to tokenize().
   */
  async mountCard(containerId, options = {}) {
    const instance = await this.initialize();

    if (instance.provider === 'stripe') {
      const card = instance.elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#1f2937',
            '::placeholder': { color: '#9ca3af' },
          },
          invalid: { color: '#E74C3C', iconColor: '#E74C3C' },
        },
        ...options,
      });

      card.mount(`#${containerId}`);

      return { provider: 'stripe', card, stripe: instance.stripe };
    }

    let pendingCard = null;
    const card = await retryPaymentInitialization(
      async () => {
        pendingCard = await instance.payments.card({
          style: {
            '.input-container': { borderColor: '#CCCCCC', borderRadius: '8px' },
            '.input-container.is-focus': { borderColor: '#f7d135' },
            '.input-container.is-error': { borderColor: '#E74C3C' },
            '.message-text': { color: '#E74C3C' },
            '.message-text.is-error': { color: '#E74C3C' },
          },
          ...options,
        });

        await pendingCard.attach(`#${containerId}`);
        return pendingCard;
      },
      {
        onRetry: async () => {
          try {
            await pendingCard?.destroy();
          } catch {
            // A partially initialized card may not have anything to destroy.
          }
          pendingCard = null;
          instance.payments = window.Square.payments(instance.applicationId, instance.locationId);
        },
      }
    );

    return { provider: 'square', card };
  }

  /**
   * Turn entered card details into a single-use token.
   *
   * Throws with a display-ready message when the card is rejected, so callers
   * can surface `error.message` directly.
   *
   * @returns {Promise<string>} Square nonce or Stripe PaymentMethod id.
   */
  async tokenize(handle) {
    if (!handle) {
      throw new Error('The payment form is still loading. Please wait a moment.');
    }

    if (handle.provider === 'stripe') {
      const { paymentMethod, error } = await handle.stripe.createPaymentMethod({
        type: 'card',
        card: handle.card,
      });

      if (error) {
        throw new Error(error.message || 'Card could not be verified.');
      }

      return paymentMethod.id;
    }

    const result = await handle.card.tokenize();

    if (result.status !== 'OK') {
      const message = result.errors?.map((e) => e.detail).filter(Boolean).join(' ');
      throw new Error(message || 'Card could not be verified.');
    }

    return result.token;
  }

  /** Tear down a mounted field so a container can be reused. */
  async destroyCard(handle) {
    if (!handle) return;

    try {
      if (handle.provider === 'stripe') {
        handle.card.unmount();
        handle.card.destroy();
      } else {
        await handle.card.destroy();
      }
    } catch {
      // A field that is already gone is not an error worth surfacing.
    }
  }
}

const paymentGateway = new PaymentGatewayService();

export default paymentGateway;
